import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '../utils/cn';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  fullWidth?: boolean;
}

const base =
  'inline-flex items-center justify-center gap-2 rounded-md font-medium transition-colors ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-octopus-400 focus-visible:ring-offset-2 ' +
  'focus-visible:ring-offset-sand-50 dark:focus-visible:ring-offset-ink-950 disabled:opacity-50 disabled:cursor-not-allowed';

const variants: Record<ButtonVariant, string> = {
  primary:
    'bg-ink-700 text-white hover:bg-ink-800 dark:bg-octopus-500 dark:hover:bg-octopus-600 dark:text-white',
  secondary:
    'bg-sand-200 text-ink-900 hover:bg-sand-300 dark:bg-ink-800 dark:text-sand-100 dark:hover:bg-ink-700',
  ghost: 'bg-transparent text-ink-700 hover:bg-sand-200 dark:text-sand-100 dark:hover:bg-ink-800',
  danger: 'bg-coral-600 text-white hover:bg-coral-700 dark:bg-coral-500 dark:hover:bg-coral-600',
};

const sizes: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-sm',
  md: 'h-10 px-4 text-sm',
  lg: 'h-12 px-6 text-base',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading = false,
    fullWidth = false,
    className,
    children,
    disabled,
    ...props
  },
  ref,
) {
  return (
    <button
      ref={ref}
      className={cn(base, variants[variant], sizes[size], fullWidth && 'w-full', className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading && (
        <span
          className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
          aria-hidden="true"
        />
      )}
      {children}
    </button>
  );
});
